"use client";

import React, { useState, useEffect } from "react";
import { Heart, Eye, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton, ProductPrice, type EcommerceProduct } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
const useWvcLanguage = () => {
  const [lang, setLang] = useState("en");
  useEffect(() => {
    const handleLangChange = () => {
      const match = document.cookie.match(/wvc_lang=([^;]+)/);
      if (match && match[1]) {
        setLang(match[1]);
      }
    };
    handleLangChange();
    window.addEventListener("languageChange", handleLangChange);
    return () => window.removeEventListener("languageChange", handleLangChange);
  }, []);
  return lang;
};
const translations = {
  en: {
    curatedEdit: "Curated Edit",
    best: "Best",
    sellers: "sellers",
    description: "Modern wardrobe foundations cut for daily ease.",
    viewAll: "View All Pieces",
    sale: "Sale",
    newIn: "New In",
    bestSeller: "Best Seller",
    quickAdd: "Quick Add",
    viewDetails: "View Details",
    loadError: "Unable to load curated products right now.",
    tryAgain: "Try Again",
    noProducts: "No pieces currently listed in this collection."
  },
  zh: {
    curatedEdit: "策划编辑",
    best: "最好的",
    sellers: "卖家",
    description: "为日常便利而设计的现代衣柜基础。",
    viewAll: "查看所有件",
    sale: "销售",
    newIn: "新进货",
    bestSeller: "畅销书",
    quickAdd: "快速添加",
    viewDetails: "查看详情",
    loadError: "目前无法加载精选产品。",
    tryAgain: "再试一次",
    noProducts: "此集合中目前未列出任何件。"
  },
  fr: {
    curatedEdit: "Édition Curée",
    best: "Meilleur",
    sellers: "vendeurs",
    description: "Fondations de garde-robe modernes coupées pour la facilité quotidienne.",
    viewAll: "Afficher Toutes les Pièces",
    sale: "Solde",
    newIn: "Nouveau",
    bestSeller: "Meilleure Vente",
    quickAdd: "Ajout Rapide",
    viewDetails: "Afficher les Détails",
    loadError: "Impossible de charger les produits curés en ce moment.",
    tryAgain: "Réessayer",
    noProducts: "Aucune pièce actuellement listée dans cette collection."
  },
  es: {
    curatedEdit: "Edición Curada",
    best: "Mejor",
    sellers: "vendedores",
    description: "Fundamentos del guardarropa moderno cortados para facilidad diaria.",
    viewAll: "Ver Todas las Piezas",
    sale: "Venta",
    newIn: "Nuevo",
    bestSeller: "Más Vendido",
    quickAdd: "Agregar Rápidamente",
    viewDetails: "Ver Detalles",
    loadError: "No se pueden cargar productos curados en este momento.",
    tryAgain: "Intentar de Nuevo",
    noProducts: "No hay piezas listadas actualmente en esta colección."
  },
  de: {
    curatedEdit: "Kuratierte Ausgabe",
    best: "Beste",
    sellers: "Verkäufer",
    description: "Moderne Garderobengrundsätze für tägliche Leichtigkeit zugeschnitten.",
    viewAll: "Alle Teile Anzeigen",
    sale: "Verkauf",
    newIn: "Neu Angekommen",
    bestSeller: "Bestseller",
    quickAdd: "Schnell Hinzufügen",
    viewDetails: "Details Anzeigen",
    loadError: "Kuratierte Produkte können derzeit nicht geladen werden.",
    tryAgain: "Erneut Versuchen",
    noProducts: "In dieser Kollektion sind derzeit keine Teile aufgeführt."
  }
};
const defaultSwatches = ["#E6D7C3", "#222222", "#7A2E3B"];
function ProductCardItem({
  product,
  index,
  language
}: {
  product: EcommerceProduct;
  index: number;
  language: keyof typeof translations;
}) {
  const [isFavorited, setIsFavorited] = useState(false);
  const [selectedColor, setSelectedColor] = useState(0);
  const images = product.gallery_image_urls || [];
  const primaryImage = product.featured_image_url || images[0] || "";
  const secondaryImage = images[1] || primaryImage;

  // Determine status badge: sale, custom tag, or alternate by index
  const isSale = product.on_sale;
  const isNew = index % 3 === 0;
  return <Card data-index={index} className="group relative flex flex-col justify-between border-0 bg-transparent p-0 shadow-none rounded-none">
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-muted">
        {/* Primary and secondary hover image */}
        {primaryImage && <img src={primaryImage} alt={product.title} loading="lazy" decoding="async" className={`h-full w-full object-cover object-center transition-[transform,opacity] duration-300 ease-out group-hover:scale-[1.03] ${secondaryImage && secondaryImage !== primaryImage ? "group-hover:opacity-0" : ""}`} />}
        {secondaryImage && secondaryImage !== primaryImage && <img src={secondaryImage} alt={`${product.title} alternate view`} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover object-center opacity-0 transition-[transform,opacity] duration-300 ease-out group-hover:scale-[1.03] group-hover:opacity-100" />}

        {/* Badge in top-left */}
        <div className="absolute top-2.5 left-2.5 z-10 flex flex-col gap-1">
          {isSale ? <Badge variant="default" className="rounded-none bg-primary px-2 py-0.5 text-[10px] font-semibold tracking-wider uppercase text-primary-foreground">
              {translations[language].sale}
            </Badge> : isNew ? <Badge variant="secondary" className="rounded-none bg-secondary px-2 py-0.5 text-[10px] font-semibold tracking-wider uppercase text-secondary-foreground">
              {translations[language].newIn}
            </Badge> : <Badge variant="outline" className="rounded-none border-border bg-accent px-2 py-0.5 text-[10px] font-semibold tracking-wider uppercase text-accent-foreground">
              {translations[language].bestSeller}
            </Badge>}
        </div>

        {/* Wishlist button in top-right */}
        <Button variant="ghost" size="icon" aria-label={isFavorited ? "Remove from wishlist" : "Add to wishlist"} onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        setIsFavorited(!isFavorited);
      }} className="absolute top-2.5 right-2.5 z-10 h-8 w-8 rounded-none border border-border/40 bg-background/80 text-foreground backdrop-blur-xs transition-colors duration-150 hover:bg-background">
          <Heart className={`h-4 w-4 transition-colors ${isFavorited ? "fill-primary text-primary" : "text-foreground"}`} />
        </Button>

        {/* Bottom Quick-Action Slide-Up Bar */}
        <div className="absolute inset-x-0 bottom-0 z-10 flex translate-y-full flex-col gap-1.5 bg-background/95 p-2 backdrop-blur-xs transition-transform duration-200 ease-out group-hover:translate-y-0">
          <AddToCartButton product={product} productId={product.id} variant="default" size="sm" text={translations[language].quickAdd} showQuantity={false} className="w-full" buttonClassName="w-full rounded-none bg-primary text-primary-foreground text-xs uppercase tracking-wider h-8 font-medium hover:bg-primary/90 transition-colors" />
          <Button asChild variant="outline" size="sm" className="h-7 w-full rounded-none border-border bg-transparent text-[11px] font-medium tracking-wider uppercase text-foreground hover:bg-muted transition-colors">
            <Link to={product.link}>
              <Eye className="mr-1.5 h-3.5 w-3.5" />
              {translations[language].viewDetails}
            </Link>
          </Button>
        </div>
      </div>

      {/* Product Information */}
      <div className="flex flex-col gap-1.5 pt-3">
        {/* Color Swatches */}
        <div className="flex items-center gap-1.5" aria-label="Available color options">
          {defaultSwatches.map((color, colorIdx) => <Button key={colorIdx} type="button" variant="ghost" size="icon" onClick={() => setSelectedColor(colorIdx)} aria-label={`Select color option ${colorIdx + 1}`} className={`h-3 w-3 min-w-0 rounded-full border border-border/80 p-0 transition-[box-shadow] ${selectedColor === colorIdx ? "ring-1 ring-primary ring-offset-1" : "opacity-80 hover:opacity-100"}`} style={{
          backgroundColor: color
        }} />)}
        </div>

        {/* Title */}
        <Link to={product.link} className="text-sm font-medium tracking-normal text-foreground line-clamp-1 hover:underline underline-offset-4">
          {product.title}
        </Link>

        {/* Price */}
        <div className="flex items-center text-xs font-normal text-muted-foreground">
          <ProductPrice product={product} />
        </div>
      </div>
    </Card>;
}
function BestsellersConsumer({
  language
}: {
  language: keyof typeof translations;
}) {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-2 gap-x-3 gap-y-6 md:grid-cols-3 md:gap-5 lg:grid-cols-4 lg:gap-7">
        {Array.from({
        length: 8
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-3">
            <div className="aspect-[3/4] w-full animate-pulse bg-muted" />
            <div className="h-3 w-16 animate-pulse bg-muted" />
            <div className="h-4 w-3/4 animate-pulse bg-muted" />
            <div className="h-3 w-1/3 animate-pulse bg-muted" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center border border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">{translations[language].loadError}</p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-4 rounded-none border-border">
          {translations[language].tryAgain}
        </Button>
      </div>;
  }
  if (!products || products.length === 0) {
    return <div className="border border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">{translations[language].noProducts}</p>
      </div>;
  }
  return <div className="grid grid-cols-2 gap-x-3 gap-y-6 md:grid-cols-3 md:gap-5 lg:grid-cols-4 lg:gap-7">
      {products.slice(0, 8).map((product, index) => <ProductCardItem key={product.id} product={product} index={index} language={language} />)}
    </div>;
}
export default function Bestsellers() {
  const currentLang = useWvcLanguage();
  const language = (currentLang in translations ? currentLang : "en") as keyof typeof translations;
  return <section id="畅销商品" data-nav="light" className="w-full bg-background text-foreground py-14 md:py-20 lg:py-24">
      <div className="mx-auto max-w-[1360px] px-6 md:px-10 lg:px-16">
        {/* Editorial Section Header */}
        <div className="mb-8 flex flex-col justify-between gap-4 border-b border-border pb-5 md:mb-12 md:flex-row md:items-end">
          <div>
            <span className="text-[11px] font-semibold tracking-[0.14em] uppercase text-muted-foreground">
              {translations[language].curatedEdit}
            </span>
            <h2 className="mt-1 font-serif text-3xl font-normal tracking-tight md:text-4xl lg:text-5xl">
              {translations[language].best} <span className="italic">{translations[language].sellers}</span>
            </h2>
            <p className="mt-2 text-sm font-light text-muted-foreground md:text-base">
              {translations[language].description}
            </p>
          </div>

          <Button asChild variant="link" className="group h-auto p-0 text-xs font-semibold tracking-wider uppercase text-foreground hover:no-underline">
            <Link to="/ShopPage" className="inline-flex items-center gap-1.5">
              <span>{translations[language].viewAll}</span>
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 ease-out group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>

        {/* Dynamic Products Grid */}
        <EcommerceProductsProvider wp_query={{
        per_page: 8,
        product_tag: [20],
        orderby: "date",
        order: "desc"
      }}>
          <BestsellersConsumer language={language} />
        </EcommerceProductsProvider>
      </div>
    </section>;
}